import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { isEqual } from 'lodash';
import findResult from '@/utils/resultsHelper';
import { clientError, firstError, firstResponseError } from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import { usersHavePermissions } from '@/users/helper';
import MemberPermissionsTable from '@/users/membership/components/MemberPermissionsTable';
import Dialog from '@/components/dialogs/Dialog';
import { cohortMembers, cohortPerms } from '../state/state';
import {
  hideShareCohort,
  fetchCohortMembers,
  removeCohortMember,
  updateCohortMemberPermissions,
  shareCohort,
  updateShareCohortProperties,
} from '../actions';
import CohortMemberAddRow from './CohortMemberAddRow';
import { cohortPermissions } from '../../constants';
import { cohortCreatorPermissions, cohortPermissionGroups } from '../constants';

const ShareCohortDialog = ({
  allowAdd,
  allowRemove,
  allowSecure,
  category,
  code,
  fetchData,
  hide,
  isDirty,
  members,
  name,
  readStatus,
  saveErrors,
  saveStatus,
  share,
  theme,
  updatePermissions,
  updateProperties,
  removeMember,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => updateProperties({ saveErrors: newErrors });

  const titleText = () => ((allowAdd || allowRemove)
    ? intl.formatMessage({ id: 'cohort.share.title' }, { name })
    : intl.formatMessage({ id: 'cohort.security.title' }, { name }));

  const titleIcon = () => ((allowAdd || allowRemove) ? 'share alternate' : 'shield');

  const handleClose = () => {
    hide(category, code);
  };

  const validateForm = () => {
    const validationErrors = [];

    if (!usersHavePermissions(members, cohortCreatorPermissions)) {
      validationErrors.push(
        clientError(
          { entity: 'cohort' },
          intl.formatMessage({ id: 'share.cohort.validation.no.owners.title' }),
          intl.formatMessage({ id: 'share.cohort.validation.owners.required' }),
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const handleDismiss = () => {
    setErrors([]);
  };

  const submitForm = () => {
    const validationErrors = checkForErrors();

    if (validationErrors.length === 0) {
      share();
    }
  };

  return (
    <Dialog
      className="aridhia-cohort-share-dialog smallwizard"
      error={firstResponseError(saveErrors)}
      icon={titleIcon()}
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={isDirty}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId="actions.apply"
      size="large"
      theme={theme}
      title={titleText()}
    >
      <div className="aridhia-user-permissions-dialog-container">
        <MemberPermissionsTable
          allowAdd={allowAdd}
          allowRemove={allowRemove}
          allowSecure={allowSecure}
          entity="cohort"
          error={firstError(saveErrors)}
          fetchData={() => fetchData(category, code)}
          members={members}
          onErrorDismiss={handleDismiss}
          permissionGroups={cohortPermissionGroups}
          readStatus={readStatus}
          removeMember={(id) => removeMember(category, code, id)}
          updatePermissions={(id, value) => updatePermissions(category, code, id, value)}
        >
          {allowAdd && (<CohortMemberAddRow category={category} code={code} allowSecure={allowSecure} />)}
        </MemberPermissionsTable>
      </div>
    </Dialog>
  );
};

const mapStateToProps = ({
  auth: { role: { permissions: { cohorts: { share, secure } } } },
  cohorts: {
    sharingCohort, cohorts,
  },
}) => {
  const { members, name, permissions } = findResult(cohorts, sharingCohort.category, sharingCohort.code) || {
    members: cohortMembers(),
    name: '',
    permissions: cohortPerms(),
  };
  const {
    results, original, readStatus, readErrors,
  } = members;

  return (
    {
      ...sharingCohort,
      allowAdd: share && permissions[cohortPermissions.INVITE_MEMBER],
      allowRemove: share && permissions[cohortPermissions.REMOVE_MEMBER],
      allowSecure: secure && permissions[cohortPermissions.SECURITY],
      isDirty: !isEqual(results, original),
      members: results,
      name,
      readErrors,
      readStatus,
    }
  );
};

const mapDispatchToProps = {
  share: shareCohort,
  hide: hideShareCohort,
  fetchData: fetchCohortMembers,
  removeMember: removeCohortMember,
  updateProperties: updateShareCohortProperties,
  updatePermissions: updateCohortMemberPermissions,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareCohortDialog);
